import type { ElectricalController } from "building-types/electrical/electricalDeviceTypes";
import type {
  LegacyRoomLayoutElectrical,
  LegacyRoomLayoutLoadConfig,
} from "building-types/electrical/electricalLegacyRoomLayoutTypes";
import type { ElectricalSlots } from "building-types/electrical/electricalSlots";
import type { Load } from "building-types/electrical/generatedElectricalTypes";
import type { Prop, PropAttributeTable } from "building-types/props/propTypes";
import type { RoomOutlines } from "building-types/rooms/roomOutlineTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { ElectricalItemCatalogSettings } from "building-types/settings/electricalItemCatalogSettings";
import type { Table } from "common/data/table";
import type { MergedPropLibrary } from "doc/propTools";
import type { RoomIdToRoomSetIndexLookup } from "doc/rooms/roomSetTypes";
import { aabb2ByPoints, aabb2Center } from "geom/aabb2";
import type { ControllerSelectionFunc } from "../controllerSelection/controllerSelectionShared";
import type { RoomSetInfoTable } from "../electricalRoomSetInfo";
import { getLoadsFromRoomLayoutLegacy } from "./electricalIndependentLoadsFromRoom";

interface ElectricalLoadPrePassForControllerSelectionFromRoomBlockDocInfo {
  readonly roomLayoutElectrical: Table<LegacyRoomLayoutElectrical>;
  readonly controllerSelectionFunc: ControllerSelectionFunc;
  readonly slots: ElectricalSlots;
  readonly roomTable: Table<Room>;
  readonly roomIdToRoomSetIndexLookup: RoomIdToRoomSetIndexLookup;
  readonly roomSetInfoTable: RoomSetInfoTable;
  readonly props: Table<Prop>;
  readonly propCatalog: PropAttributeTable;
  readonly mergedPropLibrary: MergedPropLibrary;
  readonly electricalItemCatalogSettings: ElectricalItemCatalogSettings;
  readonly roomOutlines: Table<RoomOutlines>;
  readonly mirror: boolean;
  readonly electricalControllers: Table<ElectricalController>;
}

export async function electricalLoadPrePassForControllerSelectionFromRoomBlockDoc({
  roomLayoutElectrical,
  controllerSelectionFunc,
  slots,
  roomTable,
  props,
  propCatalog,
  mergedPropLibrary,
  electricalItemCatalogSettings,
  roomOutlines,
  mirror,
  electricalControllers,
}: ElectricalLoadPrePassForControllerSelectionFromRoomBlockDocInfo) {
  for (const roomId in roomLayoutElectrical) {
    const roomGroups = roomLayoutElectrical[roomId];
    if (roomGroups && roomGroups.embeddedGroups) {
      const groups = roomGroups.embeddedGroups;
      for (const group of groups) {
        const { loads, controllers } = group;

        const result = await getLoadsAndPrimaryRoomsetFromRoomLayout(
          loads,
          slots,
          roomTable,
          roomId,
          props,
          propCatalog,
          mergedPropLibrary,
          electricalItemCatalogSettings,
          roomOutlines,
          mirror,
        );
        if (!result) {
          continue;
        }

        const { primaryRoomId } = result;

        const yo = result.loads;
        const aabb2 = aabb2ByPoints(yo.map((x) => x.position));
        const [x, y] = aabb2Center(aabb2);
        for (const { selections } of controllers) {
          await controllerSelectionFunc(selections, primaryRoomId, [x, y]);
        }
      }
    }
  }

  // new simplified electrical
  for (const controllerId in electricalControllers) {
    const controller = electricalControllers[controllerId];
    await controllerSelectionFunc(controller.selections, controller.parentId);
  }
}

interface LoadsAndPrimaryRoomSet {
  loads: Load[];
  primaryRoomId: string;
}

export async function getLoadsAndPrimaryRoomsetFromRoomLayout(
  loads: LegacyRoomLayoutLoadConfig[],
  slots: ElectricalSlots,
  roomTable: Table<Room>,
  roomId: string,
  props: Table<Prop>,
  propCatalog: PropAttributeTable,
  mergedPropLibrary: MergedPropLibrary,
  electricalItemCatalogSettings: ElectricalItemCatalogSettings,
  roomOutlines: Table<RoomOutlines>,
  mirror: boolean,
): Promise<LoadsAndPrimaryRoomSet | null> {
  const loadItems: Load[] = [];

  const room = roomTable[roomId];
  const roomProps = Object.values(props).filter((x) => x.room?.id === roomId);
  if (!room || !room.outline) {
    console.warn(`room id ${roomId} not found for outline lookup`);
    return null;
  }

  const { edgeIds } = room;

  if (loads) {
    for (const loadAssignmentOpt of loads) {
      switch (loadAssignmentOpt.type) {
        case "INDEPENDENT_BY_NAME_KEY": {
          break;
        }

        case "EMBEDDED": {
          if (loadAssignmentOpt.loads) {
            const { loads: roomLayoutLoads } = loadAssignmentOpt;
            const roomLayoutloadItems = await getLoadsFromRoomLayoutLegacy(
              roomLayoutLoads,
              slots,
              room,
              roomProps,
              propCatalog,
              mergedPropLibrary,
              electricalItemCatalogSettings,
              edgeIds,
              roomOutlines,
              mirror,
            );
            if (roomLayoutloadItems && roomLayoutloadItems.length > 0) {
              loadItems.push(...roomLayoutloadItems);
            }
          }
          break;
        }
      }
    }
  }

  if (loadItems === undefined || loadItems.length === 0) {
    // console.warn("ignoring electrical group as there aren't any loads");
    return null;
  }

  return { loads: loadItems, primaryRoomId: roomId };
}
